import { FC, useEffect, useRef, useState, useMemo } from "react";
import { Feature, Map, Overlay, View } from "ol";
import { debounce } from "lodash";
import { MapboxVectorLayer } from "ol-mapbox-style";
import VectorSource from "ol/source/Vector";
import { fromLonLat, transform, transformExtent, get as getProjection } from "ol/proj";
import { register } from "ol/proj/proj4";
import proj4 from "proj4";
import VectorLayer from "ol/layer/Vector";
import EsriJSON from "ol/format/EsriJSON";
import { bbox as bboxStrategy } from "ol/loadingstrategy";
import VectorImageLayer from "ol/layer/VectorImage";
import { emptyParcellesWorkerCode } from "./emptyParcellesWorkerCode";
import { createSitgBaseLayer } from './layers/createSitgBaseLayer';
import { createMapboxLayer } from './layers/createMapboxLayer';
import { createSitgLayer } from './layers/createSitgLayer';
import { createBiensOffMarketLayer } from './layers/createBiensOffMarketLayer';
import { createTransactionsFaoLayer } from './layers/createTransactionsFaoLayer';
import { createTransactionsFaoPrixM2Layer } from './layers/createTransactionsFaoPrixM2Layer';
import { createBiensOffMarketLoyerM2Layer } from './layers/createBiensOffMarketLoyerM2Layer';

import "ol/ol.css";
import { Point, Polygon } from "ol/geom";
import { Fill, Stroke, Style } from "ol/style";
import CircleStyle from "ol/style/Circle";
import {
  getBottomLeft,
  getBottomRight,
  getTopLeft,
  getTopRight,
  getWidth,
} from "ol/extent";
import { Coordinate } from "ol/coordinate";
import { displayNumber } from "./utils/formatters";
import { Button, Tooltip } from "antd";

import {
  RightOutlined,
  LeftOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { orderBy } from "lodash";
import axiosInstance from "./axiosIntance";
import TileLayer from "ol/layer/Tile";
import { WMTS } from "ol/source";
import WMTSTileGrid from "ol/tilegrid/WMTS";
import { createSatelliteLayer } from './layers/createSatelliteLayer';
import { createSwisstopoLayer } from './layers/createSwisstopoLayer';
import MapContextMenu, { useMapContextMenu } from './components/MapContextMenu';
import CalquesOverlay from './components/CalquesOverlay';
import ParcellePopup from './components/ParcellePopup';
import HoverPopupContent from './components/HoverPopupContent';


// Register Swiss LV95 coordinate system
proj4.defs('EPSG:2056', '+proj=somerc +lat_0=46.95240555555556 +lon_0=7.439583333333333 +k_0=1 +x_0=2600000 +y_0=1200000 +ellps=bessel +towgs84=674.374,15.056,405.346,0,0,0,0 +units=m +no_defs');
register(proj4 as any);


const MapComponent: FC<{
  onParcellesChanged: (parcelles: any[]) => void;
  onSelectParcelle: (parcelle: string | null) => void;
  center: string;
  marker: string;
  parcelleDetails: string | null;
  filters: any;
  biensOffMarketFilters: any;
  filterSidebarVisible: boolean;
  transactionsSidebarVisible?: boolean;
  showTransactionsFao?: boolean;
  setShowTransactionsFao?: (show: boolean) => void;
  onShowBiensOffMarketChange?: (show: boolean) => void;
  onShowTransactionsFaoChange?: (show: boolean) => void;
}> = ({
  parcelleDetails,
  center,
  marker,
  onParcellesChanged,
  onSelectParcelle,
  filters,
  biensOffMarketFilters,
  filterSidebarVisible,
  transactionsSidebarVisible,
  showTransactionsFao: externalShowTransactionsFao,
  setShowTransactionsFao: externalSetShowTransactionsFao,
  onShowBiensOffMarketChange,
  onShowTransactionsFaoChange,
}) => {
  const detailPopup = useRef(null);
  const titlePopup = useRef(null);

  const detailOverlay = useRef<Overlay | null>(null);
  const titleOverlay = useRef<Overlay | null>(null);

  const [mapStyle, setMapStyle] = useState("mapbox");
  const [mapStyleSelectorOpen, setMapStyleSelectorOpen] = useState(false);
  const baseLayersRef = useRef<{
    sitg: any; swisstopo: any; satellite: TileLayer<WMTS>; mapbox: any
} | null>(null);
  const overlayLayersRef = useRef<{ cadParcelle: any; cadBatiment: any; cadBatiProjet: any; emptyParcelles: any; biensOffMarket: any; biensOffMarketLoyerM2: any; transactionsFao: any; transactionsFaoPrixM2: any; customSitg?: {[key: string]: any} } | null>(null);
  const [showCadParcelles, setShowCadParcelles] = useState(false);
  const [showCadBatiments, setShowCadBatiments] = useState(false);
  const [showCadBatiProjet, setShowCadBatiProjet] = useState(false);
  const [showEmptyParcelles, setShowEmptyParcelles] = useState(false);
  const [showBiensOffMarket, setShowBiensOffMarket] = useState(false);
  const [internalShowTransactionsFao, setInternalShowTransactionsFao] = useState(true);
  const [showTransactionsFaoPrixM2, setShowTransactionsFaoPrixM2] = useState(false);
  const [showBiensOffMarketLoyerM2, setShowBiensOffMarketLoyerM2] = useState(false);
  
  // Use external state if provided, otherwise use internal state
  const showTransactionsFao = externalShowTransactionsFao !== undefined ? externalShowTransactionsFao : internalShowTransactionsFao;
  const setShowTransactionsFao = externalSetShowTransactionsFao || setInternalShowTransactionsFao;
  const [customSitgLayers, setCustomSitgLayers] = useState<{[key: string]: any}>({});
  const [sitgLayerInput, setSitgLayerInput] = useState("");
  
  // Notify parent of layer visibility changes
  useEffect(() => {
    onShowBiensOffMarketChange?.(showBiensOffMarket);
  }, [showBiensOffMarket, onShowBiensOffMarketChange]);

  useEffect(() => {
    onShowTransactionsFaoChange?.(showTransactionsFao);
  }, [showTransactionsFao, onShowTransactionsFaoChange]);
  
  // Helper function to convert hex color to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Handler for adding SITG layers
  const handleAddSitgLayer = (layerName: string) => {
    if (!customSitgLayers[layerName]) {
      const layer = createSitgLayer({
        visible: true,
        layerName: layerName,
        fillColor: hexToRgba('#00FF00', 0.1),
        strokeColor: '#00FF00',
        strokeWidth: 1.5,
        zIndex: 15
      });
      map.current.addLayer(layer);
      // Add to overlay layers ref for proper management
      if (overlayLayersRef.current?.customSitg) {
        overlayLayersRef.current.customSitg[layerName] = layer;
      }
      setCustomSitgLayers(prev => ({
        ...prev,
        [layerName]: layer
      }));
    }
  };

  // Handler for removing SITG layers
  const handleRemoveSitgLayer = (layerName: string) => {
    const layer = customSitgLayers[layerName];
    if (layer) {
      map.current.removeLayer(layer);
      // Remove from overlay layers ref
      if (overlayLayersRef.current?.customSitg) {
        delete overlayLayersRef.current.customSitg[layerName];
      }
      setCustomSitgLayers(prev => {
        const newLayers = { ...prev };
        delete newLayers[layerName];
        return newLayers;
      });
    }
  };

  // Handler for empty parcelles toggle
  const handleEmptyParcellesToggle = () => {
    setShowEmptyParcelles((v) => {
      const next = !v;
      if (next) {
        try { (overlayLayersRef.current?.emptyParcelles as any)?.setVisible(false); } catch {}
        try { (overlayLayersRef.current?.emptyParcelles as any)?.getSource()?.clear(); } catch {}
        // ensure ref is set before refresh for correct post-refresh visibility
        showEmptyParcellesRef.current = true;
        // tiny zoom-out nudge to force a render if needed
        try {
          const view = map.current.getView();
          const z = view.getZoom() ?? 0;
          view.setZoom(z - 0.0000001);
        } catch {}
        const refresher = (map.current as any)?._refreshEmptyParcelles;
        if (typeof refresher === 'function') {
          refresher();
        }
      }
      return next;
    });
  };

  // Handler for biens off-market toggle
  const handleBiensOffMarketToggle = () => {
    setShowBiensOffMarket((v) => {
      const next = !v;
      if (next) {
        // Clear any existing data first
        try { (overlayLayersRef.current?.biensOffMarket as any)?.setVisible(false); } catch {}
        try { (overlayLayersRef.current?.biensOffMarket as any)?.getSource()?.clear(); } catch {}
        
        // Tiny zoom-out nudge to force a render if needed (similar to empty parcelles)
        try {
          const view = map.current.getView();
          const z = view.getZoom() ?? 0;
          view.setZoom(z - 0.0000001);
        } catch {}
        
        const refresher = (overlayLayersRef.current?.biensOffMarket as any)?._refreshData;
        if (typeof refresher === 'function') {
          refresher(map.current, biensOffMarketFilters);
        }
      }
      return next;
    });
  };

  // Handler for transactions_fao toggle
  const handleTransactionsFaoToggle = () => {
    const next = !showTransactionsFao;
    if (next) {
      // Refresh data when showing using existing parcelles
      const updater = (overlayLayersRef.current?.transactionsFao as any)?._updateFromParcelles;
      if (typeof updater === 'function' && parcelles.length > 0) {
        updater(parcelles);
      }
    } else {
      // Clear the layer when hiding
      try { (overlayLayersRef.current?.transactionsFao as any)?.setVisible(false); } catch {}
      try { (overlayLayersRef.current?.transactionsFao as any)?.getSource()?.clear(); } catch {}
    }
    setShowTransactionsFao(next);
  };

  const handleTransactionsFaoPrixM2Toggle = () => {
    const next = !showTransactionsFaoPrixM2;
    if (next) {
      const updater = (overlayLayersRef.current?.transactionsFaoPrixM2 as any)?._updateFromParcelles;
      if (typeof updater === 'function' && parcelles.length > 0) {
        updater(parcelles);
      }
      try { (overlayLayersRef.current?.transactionsFaoPrixM2 as any)?.setVisible(true); } catch {}
    } else {
      try { (overlayLayersRef.current?.transactionsFaoPrixM2 as any)?.setVisible(false); } catch {}
      try { (overlayLayersRef.current?.transactionsFaoPrixM2 as any)?.getSource()?.clear(); } catch {}
    }
    setShowTransactionsFaoPrixM2(next);
  };

  const handleBiensOffMarketLoyerM2Toggle = () => {
    const next = !showBiensOffMarketLoyerM2;
    if (next) {
      const refresher = (overlayLayersRef.current?.biensOffMarketLoyerM2 as any)?._refreshData;
      if (typeof refresher === 'function') {
        refresher(map.current, biensOffMarketFilters);
      }
      try { (overlayLayersRef.current?.biensOffMarketLoyerM2 as any)?.setVisible(true); } catch {}
    } else {
      try { (overlayLayersRef.current?.biensOffMarketLoyerM2 as any)?.setVisible(false); } catch {}
      try { (overlayLayersRef.current?.biensOffMarketLoyerM2 as any)?.getSource()?.clear(); } catch {}
    }
    setShowBiensOffMarketLoyerM2(next);
  };
  
  const emptyParcellesRequestSeqRef = useRef(0);
  const emptyParcellesDebounceRef = useRef<number | null>(null);
  const showEmptyParcellesRef = useRef<boolean>(false);
  const emptyParcellesWorkerRef = useRef<Worker | null>(null);
  const workerBusyRef = useRef<boolean>(false);
  const workerPendingRef = useRef<boolean>(false);
  const lastWorkerStartRef = useRef<number>(0);
  const WORKER_THROTTLE_MS = 500;
  const PAGE_SIZE = 1000;


  const [hoveredParcelle, setHoveredParcelle] = useState("");
  const [parcelles, setParcelles] = useState<any[]>([]);
  const [selectedParcelle, setSelectedParcelle] = useState(null);
  const [parcelleStats, setParcelleStats] = useState<any>(null);
  const [corners, setCorners] = useState<any>({});
  const [mapExtent, setMapExtent] = useState<any>(null);
  
  // Debounced function to update map extent for better performance
  const debouncedSetMapExtent = useMemo(
    () => debounce((extent: any) => setMapExtent(extent), 100),
    []
  );
  const [zoom, setZoom] = useState(17);
  const [centerCoordinate, setCenterCoordinate] = useState<Coordinate>([
    6.1432, 46.2044,
  ]);
  const map = useRef(new Map());

  // Reference to the identified parcelles layer
  const identifiedParcellesLayer = useRef<VectorLayer<any> | null>(null);

  // Function to check if a transaction is within the visible map extent
  const isTransactionVisible = (transaction: any, extent: any) => {
    if (!transaction.position_sur_map?.coordinates || !extent) return true;
    
    const [lon, lat] = transaction.position_sur_map.coordinates;
    const [minX, minY, maxX, maxY] = extent;
    
    return lon >= minX && lon <= maxX && lat >= minY && lat <= maxY;
  };

  // Memoized calculation of visible transactions for performance
  const visibleParcelles = useMemo(() => {
    if (!mapExtent || !showTransactionsFao) {
      return showTransactionsFao ? parcelles : [];
    }
    
    const filtered = parcelles.filter(transaction => isTransactionVisible(transaction, mapExtent));
    console.log(`Filtered transactions: ${filtered.length}/${parcelles.length} visible`);
    return filtered;
  }, [parcelles, mapExtent, showTransactionsFao]);
  


  // Function to add identified parcelles to the map
  const addIdentifiedParcellesToMap = (parcelles: any[]) => {
    console.log('Adding parcelles to map:', parcelles.length);
    
    // Remove existing layer if it exists
    if (identifiedParcellesLayer.current) {
      map.current.removeLayer(identifiedParcellesLayer.current);
    }

    if (parcelles.length === 0) return;

    const features: Feature[] = [];
    
    parcelles.forEach((parcelle, index) => {
      try {
        if (parcelle.geometry && parcelle.geometry.rings && parcelle.geometry.rings.length > 0) {
          console.log(`Processing parcelle ${parcelle.value}, rings:`, parcelle.geometry.rings.length);
          
          const rings = parcelle.geometry.rings;
          const exterior = rings[0];
          
          if (exterior && exterior.length > 0) {
            console.log('Exterior coordinates sample:', exterior[0]);
            
            // The SITG coordinates are in Swiss LV95 (EPSG:2056)
            // Create polygon geometry directly with Swiss coordinates
            const polygonGeometry = new Polygon([exterior]);
            
            // Transform from Swiss LV95 to Web Mercator (EPSG:3857)
            polygonGeometry.transform('EPSG:2056', 'EPSG:3857');

            const feature = new Feature({
              geometry: polygonGeometry,
              parcelleId: parcelle.value,
              commune: parcelle.attributes?.COMMUNE,
            });

            features.push(feature);
            console.log(`Added feature for parcelle ${parcelle.value}`);
          }
        }
      } catch (error) {
        console.error('Error creating polygon for parcelle:', parcelle.value, error);
      }
    });

    console.log('Total features created:', features.length);

    if (features.length > 0) {
      const vectorSource = new VectorSource({ features });
      
      identifiedParcellesLayer.current = new VectorLayer({
        source: vectorSource,
        style: new Style({
          fill: new Fill({ 
            color: "rgba(255, 0, 0, 0.5)" 
          }),
          stroke: new Stroke({ 
            color: "#FF0000", 
            width: 3 
          })
        }),
        zIndex: 1000, // Ensure it's on top
      });

      map.current.addLayer(identifiedParcellesLayer.current);
      console.log('Layer added to map');
    }
  };

  // Function to remove identified parcelles from map
  const removeIdentifiedParcellesFromMap = () => {
    if (identifiedParcellesLayer.current) {
      map.current.removeLayer(identifiedParcellesLayer.current);
      identifiedParcellesLayer.current = null;
    }
  };

  // Context menu functionality
  const { contextMenu, setContextMenu, createContextMenuHandler, closeContextMenu } = useMapContextMenu(
    map,
    addIdentifiedParcellesToMap,
    removeIdentifiedParcellesFromMap
  );

  useEffect(() => {
    if (baseLayersRef.current) {
      baseLayersRef.current.sitg.setVisible(mapStyle==="sitg");
      baseLayersRef.current.satellite.setVisible(mapStyle === "satellite");
      baseLayersRef.current.swisstopo.setVisible(mapStyle === "swisstopo");
      baseLayersRef.current.mapbox.setVisible(mapStyle === "mapbox");
    }
  }, [mapStyle]);

  useEffect(() => {
    // keep ref in sync
    showEmptyParcellesRef.current = showEmptyParcelles;
    if (overlayLayersRef.current) {
      overlayLayersRef.current.cadParcelle.setVisible(showCadParcelles);
      overlayLayersRef.current.cadBatiment.setVisible(showCadBatiments);
      overlayLayersRef.current.cadBatiProjet.setVisible(showCadBatiProjet);
      overlayLayersRef.current.emptyParcelles.setVisible(showEmptyParcelles);
      overlayLayersRef.current.biensOffMarket.setVisible(showBiensOffMarket);
      overlayLayersRef.current.transactionsFao.setVisible(showTransactionsFao);
      overlayLayersRef.current.transactionsFaoPrixM2.setVisible(showTransactionsFaoPrixM2);
      overlayLayersRef.current.biensOffMarketLoyerM2.setVisible(showBiensOffMarketLoyerM2);
      
      if (showEmptyParcelles) {
        const refresher = (map.current as any)?._refreshEmptyParcelles;
        if (typeof refresher === 'function') {
          // hide until freshly loaded to avoid stale draw
          try { (overlayLayersRef.current.emptyParcelles as any).setVisible(false); } catch {}
          try { (overlayLayersRef.current.emptyParcelles as any).getSource()?.clear(); } catch {}
          refresher();
        }
      } else {
        // ensure cleared when turned off
        if (emptyParcellesDebounceRef.current) {
          clearTimeout(emptyParcellesDebounceRef.current);
          emptyParcellesDebounceRef.current = null;
        }
        // bump seq to invalidate in-flight responses
        emptyParcellesRequestSeqRef.current++;
        try { (overlayLayersRef.current.emptyParcelles as any).getSource()?.clear(); } catch {}
        try { (overlayLayersRef.current.emptyParcelles as any).setVisible(false); } catch {}
      }

      if (showBiensOffMarket) {
        const refresher = (overlayLayersRef.current.biensOffMarket as any)?._refreshData;
        if (typeof refresher === 'function') {
          // hide until freshly loaded to avoid stale draw
          try { (overlayLayersRef.current.biensOffMarket as any).setVisible(false); } catch {}
          try { (overlayLayersRef.current.biensOffMarket as any).getSource()?.clear(); } catch {}
          refresher(map.current, biensOffMarketFilters);
        }
      } else {
        // ensure cleared when turned off
        try { (overlayLayersRef.current.biensOffMarket as any).getSource()?.clear(); } catch {}
        try { (overlayLayersRef.current.biensOffMarket as any).setVisible(false); } catch {}
      }
    }
  }, [showCadParcelles, showCadBatiments, showCadBatiProjet, showEmptyParcelles, showBiensOffMarket]);

  // Manage custom SITG layers - ensure they stay in the map
  useEffect(() => {
    if (map.current && overlayLayersRef.current?.customSitg) {
      Object.entries(customSitgLayers).forEach(([layerName, layer]) => {
        // Check if layer is still in the map
        const mapLayers = map.current.getLayers().getArray();
        const layerInMap = mapLayers.includes(layer);
        
        if (!layerInMap && layer.getVisible()) {
          // Layer was removed from map but should be visible, re-add it
          console.log(`Re-adding layer ${layerName} to map`);
          map.current.addLayer(layer);
        }
      });
    }
  }, [customSitgLayers]);

  // Monitor map zoom changes to re-add layers if needed
  useEffect(() => {
    if (map.current) {
      const handleMoveEnd = () => {
        // Re-check custom layers after map movement/zoom
        Object.entries(customSitgLayers).forEach(([layerName, layer]) => {
          const mapLayers = map.current.getLayers().getArray();
          const layerInMap = mapLayers.includes(layer);
          
          if (!layerInMap && layer.getVisible()) {
            console.log(`Re-adding layer ${layerName} after zoom`);
            map.current.addLayer(layer);
          }
        });

        // Refresh biens off-market data when map moves
        if (showBiensOffMarket) {
          const refresher = (overlayLayersRef.current?.biensOffMarket as any)?._refreshData;
          if (typeof refresher === 'function') {
            refresher(map.current, biensOffMarketFilters);
          }
        }

        if (showBiensOffMarketLoyerM2) {
          const refresher = (overlayLayersRef.current?.biensOffMarketLoyerM2 as any)?._refreshData;
          if (typeof refresher === 'function') {
            refresher(map.current, biensOffMarketFilters);
          }
        }
      };

      map.current.on('moveend', handleMoveEnd);
      
      return () => {
        if (map.current) {
          map.current.un('moveend', handleMoveEnd);
        }
      };
    }
  }, [customSitgLayers, showBiensOffMarket, showBiensOffMarketLoyerM2, biensOffMarketFilters]);

  useEffect(() => {
    const c = center.split("|");
    setCenterCoordinate([+c[0], +c[1]]);
    reloadMarker();
    map.current.getView().setCenter(fromLonLat([+c[0], +c[1]]));
  }, [center]);

  useEffect(() => {
    reloadMarker();
  }, [marker]);

  useEffect(() => {
    const fetchParcelleStats = async (
      noParcelle: string,
      noCommune: string
    ) => {
      const response = await axiosInstance.get(
        `https://melcarnebackend.digipulse.ch/get_map_stats_parcelle?NoCommune=${noCommune}&NoParcelle=${noParcelle}&filters=${JSON.stringify(filters)}`
      );
      
      setParcelleStats(response.data);
    };

    if (detailOverlay.current && parcelleDetails) {
      const p: any = parcelles.find(
        (p: any) => p.NoParcelle === parcelleDetails
      );

      if (p) {
        setSelectedParcelle(p.NoParcelle);

        fetchParcelleStats(p.NoParcelle, p.NoCommune);

        const coordinate = fromLonLat(p.position_sur_map.coordinates);
        detailOverlay.current.setPosition(coordinate);
        // Note: Selection styling is now handled by the transactions_fao layer
      }
    }
  }, [parcelleDetails, parcelles, setParcelleStats]);

  useEffect(() => {
    const searchParcelle = async (corners: any) => {
      const apiParams = new URLSearchParams();
      apiParams.append("bl_lon", corners.bl[0]);
      apiParams.append("bl_lat", corners.bl[1]);
      apiParams.append("br_lon", corners.br[0]);
      apiParams.append("br_lat", corners.br[1]);
      apiParams.append("tl_lon", corners.tl[0]);
      apiParams.append("tl_lat", corners.tl[1]);
      apiParams.append("tr_lon", corners.tr[0]);
      apiParams.append("tr_lat", corners.tr[1]);
      apiParams.append("limit", "2000");
      apiParams.append("offset", "0");

      apiParams.append("filter", JSON.stringify(filters));

      const response = await axiosInstance.get(
        `https://melcarnedirectus2.digipulse.ch/search_by_location?${apiParams.toString()}`
      );

      const parcelles = orderBy(
        response.data.filter((p: any) => !!p.position_sur_map),
        ["NoParcelle"]
      ) as any[];
      setParcelles(parcelles);

      // Note: Transaction points are now handled by the transactions_fao layer
      // Don't call onParcellesChanged here - it will be called via useEffect when visibleParcelles changes
    };

    if (corners.bl) searchParcelle(corners);
  }, [corners, filters]);

  // Refresh biens_off_market when map extent changes
  useEffect(() => {
    if (corners.bl && showBiensOffMarket) {
      const refresher = (overlayLayersRef.current?.biensOffMarket as any)?._refreshData;
      if (typeof refresher === 'function') {
        refresher(map.current, biensOffMarketFilters);
      }
    }

    if (corners.bl && showBiensOffMarketLoyerM2) {
      const refresher = (overlayLayersRef.current?.biensOffMarketLoyerM2 as any)?._refreshData;
      if (typeof refresher === 'function') {
        refresher(map.current, biensOffMarketFilters);
      }
    }
  }, [corners, showBiensOffMarket, showBiensOffMarketLoyerM2, biensOffMarketFilters]);

  // Refresh transactions_fao when parcelles data changes
  useEffect(() => {
    if (showTransactionsFao && parcelles.length > 0) {
      const updater = (overlayLayersRef.current?.transactionsFao as any)?._updateFromParcelles;
      if (typeof updater === 'function') {
        updater(parcelles);
      }
    }
  }, [parcelles, showTransactionsFao]);

  useEffect(() => {
    if (showTransactionsFaoPrixM2 && parcelles.length > 0) {
      const updater = (overlayLayersRef.current?.transactionsFaoPrixM2 as any)?._updateFromParcelles;
      if (typeof updater === 'function') {
        updater(parcelles);
      }
    }
  }, [parcelles, showTransactionsFaoPrixM2]);

  useEffect(() => {
    const cadLayer = overlayLayersRef.current?.cadParcelle;
    const setCollisionParcelles = (cadLayer as any)?.setLabelCollisionParcelles;
    if (typeof setCollisionParcelles !== 'function') return;

    if (showTransactionsFao) {
      const parcelleNos = parcelles
        .filter((p) => p.position_sur_map?.coordinates)
        .map((p) => String(p.NoParcelle));
      setCollisionParcelles(parcelleNos);
    } else {
      setCollisionParcelles([]);
    }
  }, [parcelles, showTransactionsFao]);

  // Update parent component when visible transactions change
  useEffect(() => {
    onParcellesChanged(visibleParcelles);
  }, [visibleParcelles, onParcellesChanged]);

  function reloadMarker() {
    const layers = map.current.getLayers().getArray();
    let zIndex = 100;
    const searchResultMarkersLayer = layers.find(
      (layer) => layer.get("name") === "searchResultMarkers"
    );

    if (searchResultMarkersLayer) {
      map.current.removeLayer(searchResultMarkersLayer);
    }

    if (!marker || marker === "|") {
      return;
    }

    let features: Feature[] = [];

    // Check if marker contains neighborhood data (JSON) or just coordinates
    try {
      const neighborhoodData = JSON.parse(marker);
      
      if (neighborhoodData.type === 'neighborhood' && neighborhoodData.polygon) {
        zIndex = 1;
        // Create polygon feature for neighborhood
        const polygonCoords = neighborhoodData.polygon.map((coord: number[]) => 
          fromLonLat([coord[1], coord[0]]) // Convert [lat, lon] to [lon, lat] and transform
        );
        
        features.push(new Feature({
          geometry: new Polygon([polygonCoords]),
          type: 'neighborhood',
          name: neighborhoodData.label
        }));

        // Also add a center point
        features.push(new Feature({
          geometry: new Point(fromLonLat([+neighborhoodData.lon, +neighborhoodData.lat])),
          type: 'center'
        }));
      }
    } catch (e) {
      // Not JSON, treat as regular coordinates
      const m = marker.split("|");
      if (m[0]) {
        features.push(new Feature({
          geometry: new Point(fromLonLat([+m[0], +m[1]])),
          type: 'point'
        }));
      }
    }

    if (features.length > 0) {
      
      // Create a new Vector layer for the marker(s)
      const newLayer = new VectorLayer({
        source: new VectorSource({ features }),
        style: (feature) => {
          const featureType = feature.get('type');
          
          if (featureType === 'neighborhood') {
            return new Style({
              fill: new Fill({ 
                color: "rgba(0, 20, 130, 0.2)" 
              }),
              stroke: new Stroke({ 
                color: "#001482", 
                width: 2 
              })
            });
          } else {
            return new Style({
              image: new CircleStyle({
                radius: 8,
                fill: new Fill({ color: "#001482" }),
                stroke: new Stroke({ color: "white", width: 2 }),
              }),
            });
          }
        },
        properties: { name: "searchResultMarkers" },
        zIndex,
      });

      // Add the new layer to the map
      map.current.addLayer(newLayer);
    }
  }

  useEffect(() => {
    const matrixSet = '3857_20';        // as per <TileMatrixSetLink>
    const format = 'image/jpeg';
    const style = 'default';
    const dimensions = { Time: 'current' };

    const projection = getProjection('EPSG:3857');

    if (!projection) {
      throw new Error('EPSG:3857 projection not found');
    }

        // world extent for EPSG:3857
    const fullExtent = projection.getExtent()!;
    // compute base resolution (map width / tile pixel size)
    const tileSize = 256;
    const baseResolution = getWidth(fullExtent) / tileSize;

    const maxZoom = 20;
    const resolutions: number[] = [];
    const matrixIds: string[] = [];
    for (let z = 0; z <= maxZoom; z++) {
      resolutions[z] = baseResolution / Math.pow(2, z);
      matrixIds[z] = z.toString();
    }

    const wgs84Bbox = [5.140242, 45.398181, 11.47757, 48.230651];
    const extent3857 = transformExtent(
      wgs84Bbox,
      'EPSG:4326',
      'EPSG:3857'
    );
    const tileGrid = new WMTSTileGrid({
      origin: getTopLeft(fullExtent),
      resolutions,
      matrixIds,
      tileSize,
      extent: extent3857
    });


    const satelliteLayer = createSatelliteLayer(mapStyle === 'satellite');

    const swisstopoLayer = createSwisstopoLayer(mapStyle === 'swisstopo');

    const sitgLayer = createSitgBaseLayer(mapStyle === 'sitg');

    const cadParcelleLayer = createSitgLayer({
      visible: showCadParcelles,
      layerName: 'CAD_PARCELLE_MENSU',
      fillColor: 'rgba(255, 165, 0, 0.15)',
      strokeColor: '#FF6600',
      strokeWidth: 1.5,
      zIndex: 9,
      labelField: 'no_parcelle',
    });

    const cadBatimentLayer = createSitgLayer({
      visible: showCadBatiments,
      layerName: 'CAD_BATIMENT_HORSOL',
      fillColor: 'rgba(0, 102, 255, 0.12)',
      strokeColor: '#0066FF',
      strokeWidth: 1.25,
      zIndex: 8
    });

    const cadBatiProjet = createSitgLayer({
      visible: showCadBatiProjet,
      layerName: 'CAD_BATI_PROJET',
      fillColor: 'rgba(0, 255, 0, 0.1)',
      strokeColor: '#00FF00',
      strokeWidth: 1.5,
      zIndex: 10
    });

    // Derived layer: Parcelles without buildings
    const emptyParcellesSource = new VectorSource({ wrapX: true, useSpatialIndex: false });
    const emptyParcellesLayer = new VectorImageLayer({
      source: emptyParcellesSource,
      visible: showEmptyParcelles,
      properties: { name: 'sitg_empty_parcelles' },
      style: new Style({
        fill: new Fill({ color: 'rgba(128, 0, 128, 0.15)' }),
        stroke: new Stroke({ color: '#800080', width: 1.5 }),
      }),
      zIndex: 7,
    });

    // Biens off-market layer
    const biensOffMarketLayer = createBiensOffMarketLayer({
      visible: showBiensOffMarket,
      selectedParcelle: selectedParcelle,
    });

    // Transactions FAO layer
    const transactionsFaoLayer = createTransactionsFaoLayer({
      visible: showTransactionsFao,
      selectedParcelle: selectedParcelle,
      filters: filters,
      parcelles: parcelles,
    });

    const transactionsFaoPrixM2Layer = createTransactionsFaoPrixM2Layer({
      visible: showTransactionsFaoPrixM2,
    });

    const biensOffMarketLoyerM2Layer = createBiensOffMarketLoyerM2Layer({
      visible: showBiensOffMarketLoyerM2,
    });

    // Helper to refresh empty parcelles using both sources around current extent
    const refreshEmptyParcelles = async () => {
      try {
        const localSeq = ++emptyParcellesRequestSeqRef.current;
        const view = map.current.getView();
        const extent = view.calculateExtent(map.current.getSize());

        const bbox = extent.join(',');
        // hide layer while refreshing; clear stale features
        try { emptyParcellesLayer.setVisible(false); } catch {}
        try { emptyParcellesSource.clear(); } catch {}


        const buildParcellesUrl = (offset: number) =>
          `https://vector.sitg.ge.ch/arcgis/rest/services/Hosted/CAD_PARCELLE_MENSU/FeatureServer/0/query?where=1%3D1&outFields=egrid,type_propri&returnGeometry=true&geometry=${bbox}&geometryType=esriGeometryEnvelope&inSR=3857&outSR=3857&spatialRel=esriSpatialRelIntersects&resultOffset=${offset}&resultRecordCount=${PAGE_SIZE}&f=json`;

        const buildBatimentsUrl = (offset: number) =>
          `https://vector.sitg.ge.ch/arcgis/rest/services/Hosted/CAD_BATIMENT_HORSOL/FeatureServer/0/query?where=egrid_liste%20IS%20NOT%20NULL&outFields=egrid_liste&returnGeometry=false&geometry=${bbox}&geometryType=esriGeometryEnvelope&inSR=3857&outSR=3857&spatialRel=esriSpatialRelIntersects&resultOffset=${offset}&resultRecordCount=${PAGE_SIZE}&f=json`;

        const fetchAllPages = async (builder: (o: number) => string) => {
          let offset = 0;
          const pages: any[] = [];
          while (true) {
            // Abort if a newer request started
            if (localSeq !== emptyParcellesRequestSeqRef.current) return pages;
            const url = builder(offset);
            const json = await fetch(url).then(r => r.json());
            const features = json?.features || [];
            pages.push(json);
            if (features.length < PAGE_SIZE) break;
            offset += PAGE_SIZE;
          }
          return pages;
        };

        const [parcellesPages, batimentsPages] = await Promise.all([
          fetchAllPages(buildParcellesUrl),
          fetchAllPages(buildBatimentsUrl),
        ]);
 
        // Ignore if a newer request has started since this one
        if (localSeq !== emptyParcellesRequestSeqRef.current) {
          return;
        }

        // Throttle worker invocations
        const now = Date.now();
        if (workerBusyRef.current && now - lastWorkerStartRef.current < WORKER_THROTTLE_MS) {
          workerPendingRef.current = true;
          return;
        }
        lastWorkerStartRef.current = now;
        workerBusyRef.current = true;

        // Lazily init worker
        if (!emptyParcellesWorkerRef.current) {
          const blob = new Blob([emptyParcellesWorkerCode], { type: 'application/javascript' });
          const url = URL.createObjectURL(blob);
          emptyParcellesWorkerRef.current = new Worker(url);
          emptyParcellesWorkerRef.current.onmessage = (evt: MessageEvent) => {
            workerBusyRef.current = false;
            if (workerPendingRef.current) {
              workerPendingRef.current = false;
              // schedule another refresh to process latest extent
              if (showEmptyParcellesRef.current) {
                refreshEmptyParcelles();
              }
              return;
            }
            if (!evt.data?.ok) {
              return;
            }
            // Ignore if turned off since start
            if (!showEmptyParcellesRef.current) return;
            // Build features from rings
            const features = [] as any[];
            for (const entry of evt.data.result || []) {
              try {
                const poly = new Polygon([entry.rings]);
                // geometry is already in 3857 due to query outSR
                const feat = new Feature({ geometry: poly });
                features.push(feat);
              } catch {}
            }
            emptyParcellesSource.clear();
            if (features.length) {
              emptyParcellesSource.addFeatures(features as any);
            }
            try { emptyParcellesLayer.setVisible(true); } catch {}
          };
        }

        // Post to worker (attributes + geometry rings only)
        emptyParcellesWorkerRef.current!.postMessage({ parcellesPages, batimentsPages });

        // Ignore if a newer request has started since this one
        if (localSeq !== emptyParcellesRequestSeqRef.current) {
          return;
        }
              } catch (e) {
          // swallow worker refresh errors to avoid log noise
        }
    };
    // expose refresher for external triggers
    (map.current as any)._refreshEmptyParcelles = refreshEmptyParcelles;

    

    const mapboxLayer = createMapboxLayer(mapStyle === 'mapbox');
    // @ts-ignore: not typed
    baseLayersRef.current = { sitg: sitgLayer, satellite: satelliteLayer, mapbox: mapboxLayer, swisstopo: swisstopoLayer };
    overlayLayersRef.current = { cadParcelle: cadParcelleLayer, cadBatiment: cadBatimentLayer, cadBatiProjet: cadBatiProjet, emptyParcelles: emptyParcellesLayer, biensOffMarket: biensOffMarketLayer, biensOffMarketLoyerM2: biensOffMarketLoyerM2Layer, transactionsFao: transactionsFaoLayer, transactionsFaoPrixM2: transactionsFaoPrixM2Layer, customSitg: {} };

    

    const mapLayers = [
      // @ts-ignore: not typed
      sitgLayer,
      swisstopoLayer,
      // @ts-ignore: not typed
      satelliteLayer,
      // @ts-ignore: not typed
      mapboxLayer,
      // SITG Parcelles overlay
      // @ts-ignore: not typed
      (cadParcelleLayer as any),
      // SITG Bâtiments overlay
      cadBatimentLayer,
      // SITG Bâtiments projetés overlay
      cadBatiProjet,
      // Derived: empty parcelles
      emptyParcellesLayer,
    ];

    // Add biens off-market layer
    mapLayers.push(biensOffMarketLayer);
    mapLayers.push(biensOffMarketLoyerM2Layer);
    
    // Add transactions FAO layer
    mapLayers.push(transactionsFaoLayer);
    mapLayers.push(transactionsFaoPrixM2Layer);

    map.current = new Map({
      target: "map",
      layers: mapLayers,
      view: new View({
        center: fromLonLat(centerCoordinate),
        zoom,
        minZoom: 11,
      }),
    });

    if (map.current) {
      detailOverlay.current = new Overlay({
        element:
          detailPopup.current !== null
            ? (detailPopup.current as HTMLElement)
            : new HTMLElement(),
      });
      titleOverlay.current = new Overlay({
        element:
          titlePopup.current !== null
            ? (titlePopup.current as HTMLElement)
            : new HTMLElement(),
      });
      map.current.addOverlay(detailOverlay.current);
      map.current.addOverlay(titleOverlay.current);

      // Initialize map extent for visible transactions filtering
      const view = map.current.getView();
      const initialExtent = view.calculateExtent(map.current.getSize());
      const initialCorners = {
        bl: transform(getBottomLeft(initialExtent), "EPSG:3857", "EPSG:4326"),
        br: transform(getBottomRight(initialExtent), "EPSG:3857", "EPSG:4326"),
        tl: transform(getTopLeft(initialExtent), "EPSG:3857", "EPSG:4326"),
        tr: transform(getTopRight(initialExtent), "EPSG:3857", "EPSG:4326"),
      };
      const [minLon, minLat] = initialCorners.bl;
      const [maxLon, maxLat] = initialCorners.tr;
      setMapExtent([minLon, minLat, maxLon, maxLat]);

      map.current.on("pointermove", (evt) => {
        if (!evt.dragging) {
          map.current.getTargetElement().style.cursor = isNaN(
            map.current
              .getFeaturesAtPixel(
                map.current.getEventPixel(evt.originalEvent)
              )?.[0]
              ?.get("name")
          )
            ? ""
            : "pointer";
        }

        const feature = map.current.forEachFeatureAtPixel(
          evt.pixel,
          (feature) => feature
        );

        if (titleOverlay.current)
          if (feature) {
            // Create enhanced hover content based on feature type
            const isOffMarket = feature.get("isOffMarket");
            if (isOffMarket) {
              const hoverContent = {
                address: feature.get("address"),
                usage: feature.get("usage"),
                m2_habitable: feature.get("m2_habitable"),
                m2_terrain: feature.get("m2_terrain"),
                prix_demande: feature.get("prix_demande"),
                date_created: feature.get("date_created"),
                commentaires: feature.get("commentaires"),
                isOffMarket: true,
                            };
              setHoveredParcelle(JSON.stringify(hoverContent));
            } else {
              setHoveredParcelle(feature.get("address"));
            }
            titleOverlay.current.setPosition(evt.coordinate);
            titleOverlay.current.setPositioning("bottom-center");
          } else {
            titleOverlay.current.setPosition(undefined);
          }
      });

      map.current.on("click", (evt) => {
        // Close context menu and remove parcelles when clicking on map
        closeContextMenu();

        setParcelleStats(null);

        if (titleOverlay.current && detailOverlay.current) {
          titleOverlay.current.setPosition(undefined);
          const view = map.current.getView();
          const extent = view.calculateExtent(map.current.getSize());
          const feature = map.current.forEachFeatureAtPixel(
            evt.pixel,
            (feature) => feature
          );

          if (feature) {
            // Check if this is a biens_off_market feature
            const isOffMarket = feature.get("isOffMarket");
            const itemId = feature.get("itemId");
            
            if (isOffMarket && itemId) {
              // Open biens_off_market in directus-frontend in new tab
              const url = `https://melcarne.digipulse.ch/collections/biens_off_market?id=${itemId}`;
              window.open(url, '_blank');
              return;
            }

            // Regular transaction handling
            const selectedParcelle = feature.get("name");
            onSelectParcelle(selectedParcelle);
            setSelectedParcelle(selectedParcelle);
            const coordinate = evt.coordinate;
            detailOverlay.current.setPosition(coordinate);
            detailOverlay.current.setPositioning("bottom-center");

            // Note: Selection styling is now handled by the transactions_fao layer

            setCenterCoordinate(
              transform(coordinate, "EPSG:3857", "EPSG:4326")
            );
            //            map.current.getView().setCenter(coordinate);
            map.current.getView().animate({
              center: coordinate,
              duration: 500,
            });
          } else {
            detailOverlay.current.setPosition(undefined);
            onSelectParcelle(null);
          }
        }
      });

      map.current.on("moveend", (_) => {
        const view = map.current.getView();
        const extent = view.calculateExtent(map.current.getSize());
        const newZoom = view.getZoom()!;
        const zoomChanged = zoom !== newZoom;
        const newCenter = transform(
          view.getCenter()!,
          "EPSG:3857",
          "EPSG:4326"
        );

        const centerChanged =
          newCenter[0] !== centerCoordinate[0] ||
          newCenter[1] !== centerCoordinate[1];

        if (zoomChanged || centerChanged) {
          const corners = {
            bl: transform(getBottomLeft(extent), "EPSG:3857", "EPSG:4326"),
            br: transform(getBottomRight(extent), "EPSG:3857", "EPSG:4326"),
            tl: transform(getTopLeft(extent), "EPSG:3857", "EPSG:4326"),
            tr: transform(getTopRight(extent), "EPSG:3857", "EPSG:4326"),
          };
          setCorners(corners);
          
          // Update map extent for visible transactions filtering (debounced)
          const [minLon, minLat] = corners.bl;
          const [maxLon, maxLat] = corners.tr;
          debouncedSetMapExtent([minLon, minLat, maxLon, maxLat]);
          
          setZoom(newZoom);
          setCenterCoordinate(newCenter);
          // Update derived empty parcelles when map moves and layer is visible
          if (showEmptyParcellesRef.current) {
            if (emptyParcellesDebounceRef.current) {
              clearTimeout(emptyParcellesDebounceRef.current);
            }
            emptyParcellesDebounceRef.current = window.setTimeout(() => {
              if (showEmptyParcellesRef.current) {
                refreshEmptyParcelles();
              }
            }, 350);
          }
        }
        
      });

      // Hide empty parcelles during move to avoid stale artifacts
      map.current.on('movestart', () => {
        if (showEmptyParcellesRef.current) {
          try { (overlayLayersRef.current?.emptyParcelles as any)?.setVisible(false); } catch {}
        }
      });

      // Add context menu event listener to the map element
      const mapElement = map.current.getTargetElement();
      if (mapElement) {
        mapElement.addEventListener('contextmenu', createContextMenuHandler);

        // Store the handler for cleanup
        (map.current as any)._contextMenuHandler = createContextMenuHandler;
      }
    
    }
    reloadMarker();

    // Initial populate of derived layer if visible
    if (showEmptyParcelles) {
      refreshEmptyParcelles();
    }

    // Initial populate of biens off-market layer if visible
    if (showBiensOffMarket) {
      const refresher = (biensOffMarketLayer as any)?._refreshData;
      if (typeof refresher === 'function') {
        refresher(map.current, biensOffMarketFilters);
      }
    }

    if (showBiensOffMarketLoyerM2) {
      const refresher = (biensOffMarketLoyerM2Layer as any)?._refreshData;
      if (typeof refresher === 'function') {
        refresher(map.current, biensOffMarketFilters);
      }
    }

    // Initial populate of transactions_fao layer if visible
    if (showTransactionsFao && parcelles.length > 0) {
      const updater = (transactionsFaoLayer as any)?._updateFromParcelles;
      if (typeof updater === 'function') {
        // Use existing parcelles data
        setTimeout(() => {
          updater(parcelles);
        }, 100);
      }

      const setCollisionParcelles = (cadParcelleLayer as any)?.setLabelCollisionParcelles;
      if (typeof setCollisionParcelles === 'function') {
        const parcelleNos = parcelles
          .filter((p) => p.position_sur_map?.coordinates)
          .map((p) => String(p.NoParcelle));
        setCollisionParcelles(parcelleNos);
      }
    }

    if (showTransactionsFaoPrixM2 && parcelles.length > 0) {
      const updater = (transactionsFaoPrixM2Layer as any)?._updateFromParcelles;
      if (typeof updater === 'function') {
        setTimeout(() => {
          updater(parcelles);
        }, 100);
      }
    }
 
    // Re-add custom SITG layers after map recreation
    Object.entries(customSitgLayers).forEach(([layerName, layer]) => {
      if (layer.getVisible()) {
        map.current.addLayer(layer);
        if (overlayLayersRef.current?.customSitg) {
          overlayLayersRef.current.customSitg[layerName] = layer;
        }
      }
    });

    return () => {
      // Cleanup event listener
      const mapElement = map.current?.getTargetElement();
      const contextMenuHandler = (map.current as any)?._contextMenuHandler;
      
      if (mapElement && contextMenuHandler) {
        mapElement.removeEventListener('contextmenu', contextMenuHandler);
      }
      
      // Remove identified parcelles layer
      removeIdentifiedParcellesFromMap();
      
      if (emptyParcellesDebounceRef.current) {
        clearTimeout(emptyParcellesDebounceRef.current);
      }

      // Terminate worker to avoid leaks
      try {
        emptyParcellesWorkerRef.current?.terminate();
      } catch {}
      emptyParcellesWorkerRef.current = null;
      workerBusyRef.current = false;
      workerPendingRef.current = false;

      map.current?.setTarget(undefined);
    };
  }, [zoom, customSitgLayers]);

  const parcelleData: any[] = parcelles.filter(
    (p: any) => p.NoParcelle === selectedParcelle
  );

  return (
    <div>
      <div style={{ position: 'absolute', top: 80, right: filterSidebarVisible ? 380 : (transactionsSidebarVisible ? 370 : 20), zIndex: 1000 }}>
        <Button onClick={() => {setMapStyleSelectorOpen((v) => !v)}}>
          Calques
        </Button>
      </div>

      <CalquesOverlay
        isOpen={mapStyleSelectorOpen}
        onClose={() => setMapStyleSelectorOpen(false)}
        mapStyle={mapStyle}
        setMapStyle={setMapStyle}
        showCadParcelles={showCadParcelles}
        setShowCadParcelles={setShowCadParcelles}
        showCadBatiments={showCadBatiments}
        setShowCadBatiments={setShowCadBatiments}
        showCadBatiProjet={showCadBatiProjet}
        setShowCadBatiProjet={setShowCadBatiProjet}
        showEmptyParcelles={showEmptyParcelles}
        showBiensOffMarket={showBiensOffMarket}
        showTransactionsFao={showTransactionsFao}
        showTransactionsFaoPrixM2={showTransactionsFaoPrixM2}
        showBiensOffMarketLoyerM2={showBiensOffMarketLoyerM2}
        customSitgLayers={customSitgLayers}
        setCustomSitgLayers={setCustomSitgLayers}
        sitgLayerInput={sitgLayerInput}
        setSitgLayerInput={setSitgLayerInput}
        onAddSitgLayer={handleAddSitgLayer}
        onRemoveSitgLayer={handleRemoveSitgLayer}
        onEmptyParcellesToggle={handleEmptyParcellesToggle}
        onBiensOffMarketToggle={handleBiensOffMarketToggle}
        onTransactionsFaoToggle={handleTransactionsFaoToggle}
        onTransactionsFaoPrixM2Toggle={handleTransactionsFaoPrixM2Toggle}
        onBiensOffMarketLoyerM2Toggle={handleBiensOffMarketLoyerM2Toggle}
        hexToRgba={hexToRgba}
        filterSidebarVisible={filterSidebarVisible}
        transactionsSidebarVisible={transactionsSidebarVisible || false}
      />

      <div
        style={{
          height: "calc(100vh - 64px)",
          width: "100%",
          //cursor: "pointer",
        }}
        id="map"
        className="map-container"
      />

      {/* Context Menu */}
      <MapContextMenu
        contextMenu={contextMenu}
        onContextMenuChange={setContextMenu}
        onRemoveIdentifiedParcelles={removeIdentifiedParcellesFromMap}
      />
      
      <div ref={detailPopup} id="detail-popup" style={{ marginBottom: 17 }}>
        {parcelleData && parcelleData.length > 0 && (
          <ParcellePopup
            transactions={parcelleData.sort((t1, t2) =>
              t1.Date_Transaction > t2.Date_Transaction ? -1 : 1
            )}
            parcelleStats={parcelleStats}
          />
        )}
      </div>

      <div ref={titlePopup} id="title-popup" style={{ marginBottom: 12 }}>
        {hoveredParcelle && (
          <>
            <HoverPopupContent hoveredData={hoveredParcelle} />
            <div
              style={{
                position: "absolute",
                width: 12,
                height: 12,
                backgroundColor: "white",
                transform: "rotate(45deg)",
                bottom: 8,
                left: "calc(50% - 8px)",
              }}
            ></div>
          </>
        )}
      </div>
    </div>
  );
};

export default MapComponent;

// ParcellePopup component moved to ./components/ParcellePopup.tsx

// HoverPopupContent component moved to ./components/HoverPopupContent.tsx

// PopupField component moved to ./components/PopupField.tsx
